import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Send, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatProgressBar } from "@/components/chat/ChatProgress";
import { StreamingIndicator } from "@/components/chat/StreamingIndicator";
import { QuickSuggestions } from "@/components/chat/QuickSuggestions";
import { AgentOfflineBanner } from "@/components/chat/AgentOfflineBanner";
import { PhotoInputButton } from "@/components/chat/PhotoInputButton";
import { VoiceInputButton } from "@/components/chat/VoiceInputButton";
import type { AgentChatMessage, ChatProgress } from "@/hooks/useAgentChat";
import { useAgentChat } from "@/hooks/useAgentChat";
import { useAgentSuggestions, type Suggestion } from "@/hooks/useAgentSuggestions";
import { AssistantSkeleton } from "@/components/chat/AssistantSkeleton";

interface AssistantConversationProps {
  className?: string;
}

type PendingImage = { base64: string; mimeType: string };

/** Render one message: streaming placeholder → indicator, otherwise a bubble. */
function ConversationItem({
  message,
  onQuickAction,
  onRetry,
}: {
  message: AgentChatMessage;
  onQuickAction: (text: string) => void;
  onRetry: () => void;
}) {
  if (message.isStreaming && !message.content && !message.isError) {
    return <StreamingIndicator activeToolCalls={message.activeToolCalls} hasText={false} />;
  }
  return <ChatMessage message={message} onQuickAction={onQuickAction} onRetry={onRetry} />;
}

/** Empty-state hint shown before the first message. */
function ConversationEmptyState() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 py-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Sparkles className="h-6 w-6" />
      </div>
      <p className="text-sm text-muted-foreground">{t("aiAssistant.emptyState")}</p>
    </div>
  );
}

/** Thumbnail + remove control for a pending photo attachment. */
function PendingImagePreview({ image, onRemove }: { image: PendingImage; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 rounded-lg border border-outline-variant bg-card p-2">
      <img
        src={`data:${image.mimeType};base64,${image.base64}`}
        alt={t("inspectionChat.photo.preview")}
        className="h-12 w-12 rounded object-cover"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-auto text-muted-foreground"
        onClick={onRemove}
      >
        {t("common.delete")}
      </Button>
    </div>
  );
}

/** Offline banner — renders nothing while online. */
function OfflineRegion({ isOnline }: { isOnline: boolean }) {
  if (isOnline) return null;
  return (
    <div className="px-4 pt-2">
      <AgentOfflineBanner />
    </div>
  );
}

/** Inspection-flow progress bar — renders nothing when no progress is tracked. */
function ProgressRegion({
  progress,
  isStreaming,
}: {
  progress: ChatProgress | null;
  isStreaming: boolean;
}) {
  if (!progress) return null;
  return (
    <div className="border-b border-outline-variant px-4 py-2">
      <ChatProgressBar progress={progress} status={isStreaming ? "streaming" : "idle"} />
    </div>
  );
}

/** Scrollable message list (empty-state + items). */
function MessageList({
  messages,
  showEmpty,
  onQuickAction,
  onRetry,
  scrollRef,
}: {
  messages: AgentChatMessage[];
  showEmpty: boolean;
  onQuickAction: (text: string) => void;
  onRetry: () => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <ScrollArea className="flex-1 px-4" ref={scrollRef}>
      <div className="space-y-4 py-4">
        {showEmpty && <ConversationEmptyState />}
        {messages.map((message) => (
          <ConversationItem
            key={message.id}
            message={message}
            onQuickAction={onQuickAction}
            onRetry={onRetry}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

/** Input row: suggestions + photo attach + text + send. */
function InputBar({
  suggestions,
  suggestionsLoading,
  showSuggestions,
  pendingImage,
  setPendingImage,
  onSend,
  isLoading,
  sendEnabled,
}: {
  suggestions: Suggestion[] | undefined;
  suggestionsLoading: boolean;
  showSuggestions: boolean;
  pendingImage: PendingImage | null;
  setPendingImage: (img: PendingImage | null) => void;
  onSend: (text: string) => void;
  isLoading: boolean;
  sendEnabled: boolean;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");

  const submit = (text: string) => {
    onSend(text);
    setInput("");
  };

  // Append a voice transcript to the current input (does NOT auto-send — the
  // user can review/edit before submitting). Adds a separating space if needed.
  const appendTranscript = (text: string) => {
    setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text));
  };

  // Require non-empty text: onSend only carries text, so an image-only state would
  // dispatch an empty message (and drop the image). Image input is not yet backend-supported.
  const canSend = input.trim().length > 0 && sendEnabled;

  return (
    <div className="space-y-2 border-t border-outline-variant bg-surface-container-low p-4">
      {showSuggestions && (
        <QuickSuggestions
          suggestions={suggestions}
          isLoading={suggestionsLoading}
          onSelect={submit}
          disabled={!sendEnabled}
        />
      )}

      {pendingImage && (
        <PendingImagePreview image={pendingImage} onRemove={() => setPendingImage(null)} />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        className="flex items-center gap-2"
      >
        <PhotoInputButton onImageSelected={setPendingImage} disabled={!sendEnabled} />
        <VoiceInputButton onTranscript={appendTranscript} disabled={!sendEnabled} />
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("aiAssistant.inputPlaceholder")}
          disabled={!sendEnabled}
          aria-label={t("aiAssistant.inputPlaceholder")}
          className="flex-1"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!canSend}
          aria-label={t("aiAssistant.send")}
          className="shrink-0"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}

/**
 * The shared AI assistant conversation surface (TES-1128, WP4).
 *
 * Holds the scrollable message list, role-aware quick suggestions, the
 * streaming "thinking" indicator, the offline banner, progress bar, and the
 * input row (text + photo). Reused by both the desktop docked panel and the
 * mobile bottom sheet so the behaviour is identical across viewports.
 *
 * Photo input is IN scope (DS-v2 + WP4). The current edge layer rejects images
 * with HTTP 422; `useAgentChat` translates that into a friendly capability
 * message, so attaching a photo never crashes the panel.
 */
export function AssistantConversation({ className }: AssistantConversationProps) {
  const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
  const {
    messages,
    isLoading,
    isStreaming,
    isRestoring,
    sendMessage,
    progress,
    sendEnabled,
    isOnline,
  } = useAgentChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserMessage = useRef<string>("");

  // Suggestions pause while a turn is streaming so they refresh after each turn.
  const { data: suggestions, isLoading: suggestionsLoading } = useAgentSuggestions(!isStreaming);

  // Auto-scroll to the newest message. The ScrollArea ref points at the Radix
  // Root (overflow:hidden); the actually-scrollable node is the inner Viewport.
  useEffect(() => {
    const viewport = scrollRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [messages, isLoading]);

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !sendEnabled) return;
      lastUserMessage.current = trimmed;
      const image = pendingImage ?? undefined;
      setPendingImage(null);
      void sendMessage(trimmed, image ? [image] : undefined);
    },
    [sendEnabled, sendMessage, pendingImage],
  );

  const handleRetry = useCallback(() => {
    if (lastUserMessage.current) handleSend(lastUserMessage.current);
  }, [handleSend]);

  if (isRestoring) return <AssistantSkeleton />;

  const isEmpty = messages.length === 0;
  const lastIsStreaming = messages[messages.length - 1]?.isStreaming === true;
  const hasSuggestions = !!suggestions && suggestions.length > 0;

  return (
    <div className={className}>
      <OfflineRegion isOnline={isOnline} />
      <ProgressRegion progress={progress} isStreaming={isStreaming} />
      <MessageList
        messages={messages}
        showEmpty={isEmpty && !lastIsStreaming}
        onQuickAction={handleSend}
        onRetry={handleRetry}
        scrollRef={scrollRef}
      />
      <InputBar
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        showSuggestions={isEmpty || hasSuggestions}
        pendingImage={pendingImage}
        setPendingImage={setPendingImage}
        onSend={handleSend}
        isLoading={isLoading}
        sendEnabled={sendEnabled}
      />
    </div>
  );
}
